import i18next from 'i18next';
import z from 'zod';

import { PlanAssignment } from '@cfa/pathway-fe-shared/xp';

export const generateTotalTime = (
  totalMinutes: number,
  hour: string,
  mins: string,
) => {
  const timeSpentHours = Number.isInteger(totalMinutes)
    ? Math.floor(totalMinutes / 60)
    : 0;
  const timeSpentMinutes = Number.isInteger(totalMinutes)
    ? Math.floor(totalMinutes % 60)
    : 0;
  const timeSpentOnPlanString =
    timeSpentHours > 0
      ? `${timeSpentHours} ${hour} ${timeSpentMinutes} ${mins}`
      : `${timeSpentMinutes} ${mins}`;
  return timeSpentOnPlanString;
};

export const getTimeString = (time: number) => {
  const timeSpentHrs = Math.floor(time / 60);
  const timeSpentMins = time % 60;
  return `${
    timeSpentHrs > 0 ? `${timeSpentHrs} ${i18next.t('Generic.hour')}` : ''
  } ${timeSpentMins} ${i18next.t('Generic.mins')}`.trim();
};

/**
 * @deprecated Reads time from PlanAssignment.steps[].duration; migrate callers to use getTimeSpentOnPlan with XP UserPlanStatus. Will be removed in PATHWAY-3174.
 */
export const getTimeSpentOnPlanForTeamMember = (status?: PlanAssignment) => {
  if (!status?.steps?.length) {
    return 0;
  }
  const timeSpentMins = status.steps.reduce(
    (acc: number, step) => acc + Math.round((step.duration ?? 0) / 60),
    0,
  );
  return Math.round(timeSpentMins);
};

const stepSchema = z.array(z.object({ duration: z.number() }));

const sumStepDurationMins = (steps: unknown): number => {
  const parsed = stepSchema.safeParse(steps);
  if (!parsed.success) return 0;
  return parsed.data.reduce(
    (acc, step) => acc + Math.round(step.duration / 60),
    0,
  );
};

// @TODO: Replace Record<string, unknown> with proper plan types once plan type refactor is complete (PATHWAY-3174)
export const getTimeSpentOnPlan = (plan: Record<string, unknown>) => {
  const hasTimeTaken =
    'timeTaken' in plan && typeof plan.timeTaken === 'number';
  const rawDuration = 'duration' in plan ? Number(plan.duration) : undefined;
  const hasDuration = rawDuration !== undefined && Number.isFinite(rawDuration);
  const hasSteps = 'steps' in plan;

  if (!hasTimeTaken && !hasDuration && !hasSteps) {
    return null;
  }

  const totalMins = hasTimeTaken
    ? getMinsFromTotalSeconds(plan.timeTaken as number)
    : hasDuration
      ? getMinsFromTotalSeconds(rawDuration)
      : sumStepDurationMins(plan.steps);

  return getTimeString(totalMins);
};

export const getHrsFromTotalMins = (totalMinutes: number) =>
  Math.floor(totalMinutes / 60);

export const getMinsFromTotalMins = (totalMinutes: number) => totalMinutes % 60;

export const getMinsFromTotalSeconds = (totalSeconds: number) =>
  Math.round(totalSeconds / 60);

export const getDurationFromTotalSeconds = (totalSeconds: number) => {
  const totalMins = getMinsFromTotalSeconds(totalSeconds);
  return {
    hours: getHrsFromTotalMins(totalMins),
    minutes: getMinsFromTotalMins(totalMins),
  };
};
